import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

type RecorderStatus = "idle" | "recording" | "sending" | "error";

type UseAudioRecordingOptions = {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  onToggle?: (isRecording: boolean) => void;
  onReady?: (blob: Blob, mimeType: string) => Promise<void> | void;
};

const supportedMimeTypes = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

const pickSupportedMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }
  for (const mimeType of supportedMimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return null;
};

export function useAudioRecording({
  canvasRef,
  onToggle,
  onReady,
}: UseAudioRecordingOptions) {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const skipOnReadyRef = useRef(false);
  const isRecordingRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const barsRef = useRef<number[]>([]);
  const barCountRef = useRef<number>(0);
  const bucketStartRef = useRef<number | null>(null);
  const bucketIndexRef = useRef<number>(0);
  const canvasSizeRef = useRef<{ width: number; height: number; dpr: number }>({
    width: 0,
    height: 0,
    dpr: 1,
  });

  const clearAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const cleanupAudio = useCallback(async () => {
    clearAnimation();
    if (mediaStreamRef.current) {
      for (const track of mediaStreamRef.current.getTracks()) {
        track.stop();
      }
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, [clearAnimation]);

  const drawRealtimeWaveform = useCallback(() => {
    if (!isRecordingRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      console.warn('No canvas found')
      return;
    }

    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) {
      return;
    }

    const analyser = analyserRef.current;
    let level = 0;
    if (analyser) {
      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteTimeDomainData(dataArray);

      let peak = 0;
      let sumSquares = 0;
      for (let i = 0; i < bufferLength; i += 1) {
        const normalized = Math.abs((dataArray[i] ?? 128) - 128) / 128;
        sumSquares += normalized * normalized;
        if (normalized > peak) {
          peak = normalized;
        }
      }
      const rms = Math.sqrt(sumSquares / bufferLength);
      level = Math.max(peak, rms * 2.2);
    }
    const now = performance.now();

    const { width, height } = canvas;
    if (!width || !height) {
      return;
    }
    canvasCtx.clearRect(0, 0, width, height);

    const barWidth = 3;
    const barGap = 4;
    const windowMs = 4000;
    const minBar = 0;
    const barCount = Math.floor(width / (barGap + barWidth));
    const bucketMs = windowMs / barCount;

    if (barCountRef.current !== barCount) {
      barCountRef.current = barCount;
      barsRef.current = new Array<number>(barCount).fill(minBar);
      bucketStartRef.current = now;
      bucketIndexRef.current = 0;
    }

    if (bucketStartRef.current === null) {
      bucketStartRef.current = now;
      bucketIndexRef.current = 0;
      barsRef.current = new Array<number>(barCount).fill(minBar);
    }

    let bucketStart = bucketStartRef.current;
    let bucketIndex = bucketIndexRef.current;
    while (now - bucketStart >= bucketMs) {
      bucketStart += bucketMs;
      bucketIndex = (bucketIndex + 1) % barCount;
      barsRef.current[bucketIndex] = minBar;
    }
    bucketStartRef.current = bucketStart;
    bucketIndexRef.current = bucketIndex;

    barsRef.current[bucketIndex] = Math.max(barsRef.current[bucketIndex] ?? minBar, level);

    const mid = height / 2;
    let x = 0;
    for (let i = 0; i < barCount; i += 1) {
      const index = (bucketIndex + 1 + i) % barCount;
      const value = barsRef.current[index] ?? minBar;
      const scaled = Math.min(1, Math.pow(value, 0.6) * 1.8);
      const barHeight = Math.max(2, Math.round(scaled * height));
      canvasCtx.fillStyle = "rgba(255, 255, 255, 0.85)";
      canvasCtx.fillRect(x, mid - barHeight / 2, barWidth, barHeight);
      x += barWidth + barGap;
      if (x > width) {
        break;
      }
    }

    if (isRecordingRef.current) {
      rafRef.current = requestAnimationFrame(drawRealtimeWaveform);
    }
  }, [canvasRef]);

  const startRecording = useCallback(async () => {
    setErrorMessage(null);
    setStatus("recording");
    setIsRecording(true);
    isRecordingRef.current = true;
    skipOnReadyRef.current = false;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      throw new Error("Audio recording is not supported in this browser.");
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    const mimeType = pickSupportedMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];
    barsRef.current = [];
    barCountRef.current = 0;
    bucketStartRef.current = null;
    bucketIndexRef.current = 0;

    const canvas = canvasRef.current;
    if (canvas) {
      const canvasCtx = canvas.getContext("2d");
      if (canvasCtx) {
        const { width, height } = canvasSizeRef.current;
        canvasCtx.clearRect(0, 0, width, height);
      }
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.start();

    recorder.onstop = async () => {
      try {
        const shouldSkip = skipOnReadyRef.current;
        skipOnReadyRef.current = false;
        clearAnimation();
        isRecordingRef.current = false;
        setStatus("sending");
        if (!shouldSkip) {
          const blob = new Blob(chunksRef.current, {
            type: recorder.mimeType || "audio/webm",
          });
          if (onReady) {
            await onReady(blob, blob.type || "audio/webm");
          }
        }
        setStatus("idle");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to transcribe audio.";
        setErrorMessage(message);
        setStatus("error");
      } finally {
        setIsRecording(false);
        await cleanupAudio();
      }
    };

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    source.connect(analyser);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    drawRealtimeWaveform();
  }, [canvasRef, cleanupAudio, drawRealtimeWaveform, onReady]);

  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      isRecordingRef.current = false;
      clearAnimation();
      mediaRecorderRef.current.stop();
    } else {
      await cleanupAudio();
      setIsRecording(false);
      isRecordingRef.current = false;
      clearAnimation();
      setStatus("idle");
    }
  }, [cleanupAudio, clearAnimation]);

  const cancel = useCallback(async () => {
    if (status === "sending") {
      return;
    }
    skipOnReadyRef.current = true;
    await stopRecording();
    setErrorMessage(null);
    setStatus("idle");
  }, [status, stopRecording]);

  const toggle = useCallback(async () => {
    if (status === "sending") {
      return;
    }
    try {
      if (isRecording) {
        await stopRecording();
        onToggle?.(false);
      } else {
        isRecordingRef.current = true;
        drawRealtimeWaveform();
        await startRecording();
        onToggle?.(true);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to start recording.";
      setErrorMessage(message);
      setStatus("error");
      setIsRecording(false);
      isRecordingRef.current = false;
      onToggle?.(false);
      await cleanupAudio();
    }
  }, [cleanupAudio, isRecording, onToggle, startRecording, status, stopRecording]);

  useEffect(() => {
    return () => {
      void cleanupAudio();
    };
  }, [cleanupAudio]);

  return { status, isRecording, errorMessage, toggle, cancel };
}
